const ERROR = 10;
const WARNING = 20;
const TEMP_DEBUG = 25;
const DEBUG = 30;
const VERBOSE = 40;
const DISABLED = 100;

let maxLevelToDisplay = TEMP_DEBUG;

const defaultLevelForCategory: Partial<Record<LoggingCategory, number>> = {};

export const loggingCategories = [
    'automatic-layout',
    "background-scheduler-worker",
    "background-scheduling-service",
    "background-scheduling-service-error",
    "burndown",
    "drag-drop",
    "duration-converting",
    "error",
    "feedback",
    "gameplan_server",
    "gameplan_server_connection",
    "gantt",
    "general",
    "home.projects",
    "image-cache",
    "intelligence",
    "layout",
    "model",
    "model-deleting",
    "model-grouping",
    "model-selection",
    "plan-reference",
    "resource-list",
    "scenario",
    "scenario-summary-aggregates",
    "scheduling",
    "session",
    "setup",
    "storage",
    "subscriptions",
    "tool-move",
    "tools",
    "ui-layout",
    "ui-MRU",
    "ui-properties",
    "ui-properties-groups",
    "ui-properties-resources",
    "ui-properties-tasks",
    "ui-settings",
    "undo-redo",
    "util-graph",
    "util-sync",
    "workspace",
    "gantt-percent-complete",
    "workspace-groups",
] as const;


interface CategoryLoggingSettings {
    color?: string;
    prefix?: string;
}

const loggingOptions: Partial<Record<LoggingCategory, CategoryLoggingSettings>> = {};


loggingOptions['background-scheduler-worker'] = {
    color: 'background: white; color: blue',
};

loggingOptions['background-scheduling-service'] = {
    color: 'background: blue; color: white',
    prefix: 'BackgroundSchedulingService',
};


loggingOptions['background-scheduling-service-error'] = {
    color: 'background: red; color: white',
    prefix: 'BackgroundSchedulingService',
};

loggingOptions['gantt-percent-complete'] = {
    color: 'background: #2d5a27; color: white',
    prefix: 'GanttPctComplete',
};

export type LoggingCategory = typeof loggingCategories[number];

export const storage: LoggingCategory = "storage";
export const undo: LoggingCategory = "undo-redo";
export const dragDrop: LoggingCategory = "drag-drop";
export const burndownChart: LoggingCategory = "burndown";
export const gantt: LoggingCategory = "gantt";
export const model: LoggingCategory = "model";
export const model_grouping: LoggingCategory = "model-grouping";
export const model_selection: LoggingCategory = "model-selection";
export const model_deleting: LoggingCategory = "model-deleting";
export const subscriptions: LoggingCategory = "subscriptions";
export const scheduling: LoggingCategory = "setup";
export const gameplan_server_connection: LoggingCategory = "gameplan_server_connection";
export const gameplan_server: LoggingCategory = "gameplan_server";
export const session: LoggingCategory = "session";
export const scenario: LoggingCategory = "scenario";
export const resourceList: LoggingCategory = "resource-list";
export const scenario_summary_aggregates: LoggingCategory = "scenario-summary-aggregates";
export const ui_settings: LoggingCategory = "ui-settings";
export const ui_properties: LoggingCategory = "ui-properties";
export const ui_properties_groups: LoggingCategory = "ui-properties-groups";
export const ui_properties_resources: LoggingCategory = "ui-properties-resources";
export const ui_properties_tasks: LoggingCategory = "ui-properties-tasks";
export const tool_move: LoggingCategory = "tool-move";
export const tools: LoggingCategory = "tools";
export const image_cache: LoggingCategory = "image-cache";
export const util_sync: LoggingCategory = "util-sync";
export const util_graph: LoggingCategory = "util-graph";
export const ui_MRU: LoggingCategory = "ui-MRU";
export const workspace: LoggingCategory = "workspace";
export const workspace_groups: LoggingCategory = "workspace-groups";
export const feedback: LoggingCategory = "feedback";
export const duration_converting: LoggingCategory = "duration-converting";
export const error: LoggingCategory = "error";

defaultLevelForCategory[error] = ERROR;

// It's important we know about errors in tools. Things can be very hard to track down without these errors.
defaultLevelForCategory[tools] = ERROR;

defaultLevelForCategory[image_cache] = ERROR;

// Leave this enabled because it is so fundamental to how the model updating works.
defaultLevelForCategory[util_sync] = ERROR;

defaultLevelForCategory['background-scheduling-service-error'] = ERROR;

const alwaysLogCategory: Partial<Record<LoggingCategory, boolean>> = {};
// alwaysLogCategory['gantt-percent-complete'] = true;


export function enableForcedLoggingOfCategory(category: LoggingCategory) {
    alwaysLogCategory[category] = true;
}

export function disbleForcedLoggingOfCategory(category: LoggingCategory) {
    alwaysLogCategory[category] = false;
}


function logInternal(category: LoggingCategory, level: number, ...args: unknown[]): void {
    if (alwaysLogCategory[category] || (level !== undefined && level <= maxLevelToDisplay)) {
        const prefix = loggingOptions[category]?.prefix ?? category;
        const color = loggingOptions[category]?.color;

        const newArgs = color
            ? [`%c${prefix}: `, color, ...args]
            : [`${prefix}: `, ...args];
        //console.log.apply<Console, any[], void>(console, newArgs);

        console.log(...newArgs); // avoid wrapping args in an array
    }
}


export function log(category: LoggingCategory, ...args: unknown[]): void {
    const level = defaultLevelForCategory[category];
    logInternal(category, level || DEBUG, ...args);
}

export interface ILogger {
    debug: (...args: unknown[]) => void;
    warning: (...args: unknown[]) => void;
    error: (...args: unknown[]) => void;
}

export function getLogger(category: LoggingCategory): ILogger {
    return {
        debug: (...args: unknown[]) => logInternal(category, DEBUG, ...args),
        warning: (...args: unknown[]) => logInternal(category, WARNING, ...args),
        error: (...args: unknown[]) => logInternal(category, ERROR, ...args),
    };
}


